import { ChangeEvent } from "react";
import axiosClient from "../../axiosClient";
import { ClipLoader } from "react-spinners";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PageHeader from "../../components/PageHeader/PageHeader";

interface InviteUserValues {
  userEmail: string;
}

const InviteUser = () => {
  const appOrigin = window.location.origin;

  const inviteUserSchema = Yup.object().shape({
    userEmail: Yup.string().email("Invalid email").required("Email required"),
  });

  return (
    <>
      <PageHeader backLink="/admin-dashboard" pageTitle="Invite User" />
      <div className="p-4">
        <h2 className="mb-2 font-bold text-gray-900 text-xl tracking-tight">
          Invite User To Platform
        </h2>
        <Formik<InviteUserValues>
          initialValues={{
            userEmail: "",
          }}
          validationSchema={inviteUserSchema}
          onSubmit={async (values, { setStatus }) => {
            setStatus("");
            const email = values.userEmail;
            try {
              const inviteLink = `${appOrigin}/sign-up?email=${email}`;

              const inviteRes = await axiosClient.post(
                "/api/users/send-invite",
                {
                  email,
                  inviteLink,
                }
              );

              if (inviteRes.status !== 200) {
                setStatus({
                  error:
                    inviteRes.data?.message ||
                    "User invited but failed to send email. Check API keys.",
                });
              } else {
                setStatus({
                  success: `User invited successfully! An email has been sent to them.\n(Link: ${inviteLink})`,
                });
              }
            } catch (error) {
              const err = error as {
                response?: { data?: { message?: string } };
                message?: string;
              };
              setStatus({
                error:
                  err.response?.data?.message ||
                  `Error inviting user:\n${err.message || String(error)}`,
              });
            }
          }}
        >
          {({ isSubmitting, status, setFieldValue, setStatus, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <div className="rounded-md">
                <div>
                  <label
                    htmlFor="userEmail"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email
                  </label>
                  <Field
                    id="userEmail"
                    name="userEmail"
                    type="email"
                    autoComplete="off"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    placeholder="Email"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setFieldValue("userEmail", e.target.value);
                      setStatus({ error: "" });
                    }}
                  />
                </div>
              </div>
              <button
                type="submit"
                className="mt-2 group relative flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:pointer-events-none"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <ClipLoader
                    color="white"
                    speedMultiplier={0.6}
                    size={"1.25rem"}
                  />
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4" />
                    Invite
                  </>
                )}
              </button>
              <div className="mt-2 min-h-[1.25rem]">
                <ErrorMessage name="userEmail">
                  {(msg) => <div className="text-sm text-red-600">*{msg}</div>}
                </ErrorMessage>
              </div>
              {status && status.error ? (
                <div className="mt-2 text-sm text-red-600 whitespace-pre-wrap">
                  *{status.error}
                </div>
              ) : null}
              {status && status.success ? (
                <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                  {status.success}
                </div>
              ) : null}
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default InviteUser;
